import axios from 'axios';
import { env } from '@/shared/config/env';
import { ROUTES } from '@/shared/config/constants';
import { useAuthStore } from '@/entities/session/model/authStore';

/**
 * Centralized Axios instance.
 *
 * Request interceptor injects Bearer token + X-Request-Id.
 * Response interceptor:
 *   - 401 on non-auth-flow paths -> clear auth + redirect to /login.
 *   - All errors normalized to { status, code, message, raw }.
 */

export const api = axios.create({
  baseURL: env.API_BASE,
  timeout: 90_000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    config.headers.set('X-Request-Id', crypto.randomUUID());
  }
  if (config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  return config;
});

const AUTH_FLOW_PATHS = ['/auth/otp/request', '/auth/otp/verify', '/auth/login'];

// Mock-demo mode (dev build + MSW). Folds to false in a real production build, so
// the 401 → logout behavior below is unchanged in production (and tree-shaken).
const DEMO_MOCKS = import.meta.env.DEV && env.ENABLE_MSW;

function isAuthFlowRequest(url) {
  if (!url) return false;
  return AUTH_FLOW_PATHS.some((p) => url.includes(p));
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status ?? 0;
    const reqUrl = error?.config?.url;

    if (status === 401 && !isAuthFlowRequest(reqUrl) && !DEMO_MOCKS) {
      useAuthStore.getState().clearAuth();
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      if (path !== ROUTES.LOGIN && path !== ROUTES.OTP) {
        window.location.assign(`${ROUTES.LOGIN}?reason=session_expired`);
      }
    }

    const normalized = {
      status,
      code: error?.response?.data?.code ?? 'UNKNOWN',
      message: error?.response?.data?.message ?? error?.message ?? 'Request failed',
      raw: error,
    };
    return Promise.reject(normalized);
  }
);
